import { getItem } from '@propertypal/shared/src/services/local.storage';
import {
  BooleanKey,
  BuyerType,
  LegalFeesContact,
  LegalFeesFormValue,
  LegalFeesResponse,
} from '@propertypal/shared/src/types/legal-fees';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import { formatNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import { useEffect, useState } from 'react';
import useCachedUser, { FormDetails } from './useCachedUser';

export const LEGAL_FEES_STORAGE_KEY = 'legalFees';
export const LEGAL_FEES_RESULT_STORAGE_KEY = 'legalFeesResult';

const useCacheLegalFees = (
  profile: WebsiteUser | undefined,
  price: number | undefined,
  buyingStatus: string | null,
  ukResident: '' | BooleanKey | null,
  standalone: boolean,
  getResult: boolean,
  setResult: (result: LegalFeesResponse | null) => void,
  formCallback: (values: LegalFeesFormValue) => void,
  contactCallback: (values: LegalFeesContact) => void,
) => {
  const [userProfile, setUserProfile] = useState<FormDetails | null>();

  useCachedUser(profile, (data) => setUserProfile(data));

  useEffect(() => {
    const cache = getItem<LegalFeesFormValue | null>(LEGAL_FEES_STORAGE_KEY);

    let values: LegalFeesFormValue = {};
    let contact: LegalFeesContact = {};

    if (buyingStatus) values.stampDutyType = buyingStatus as BuyerType;

    if (ukResident) values.ukResident = ukResident;

    if (cache) {
      values = {
        ...values,
        ...cache,
      };
    }

    if (price) values.purchasePrice = formatNumber(price);

    if (userProfile) {
      contact = {
        ...contact,
        name: userProfile?.name,
        email: userProfile?.email,
        prefix: userProfile?.prefix,
        telephone: userProfile?.phoneNumberNoPrefix,
      };
    }

    // clear expectedSalePrice & purchasePrice fields on standalone page
    if (standalone) {
      values.expectedSalePrice = '';
      values.purchasePrice = '';
    }

    // if a first time buyer and selected buying and selling, clear buyingType
    if (values.buyingType === 'BUYING_AND_SELLING' && values.stampDutyType === 'FIRST_TIME_BUYER')
      values.stampDutyType = '';

    if (Object.keys(values).length) formCallback(values);
    if (Object.keys(contact).length) contactCallback(contact);
  }, [userProfile]);

  useEffect(() => {
    if (getResult) {
      const result = getItem<LegalFeesResponse | null>(LEGAL_FEES_RESULT_STORAGE_KEY);
      if (result) setResult(result);
    }
  }, [getResult, setResult]);
};

export default useCacheLegalFees;
